import type { AppVersion } from '@/types'
import { applyCreateSpaceContext } from '@/pages/app-detail/versions/drawer/formModel'

const STORAGE_KEY = 'hibro.createSpaceLock'

export interface CreateSpaceLock {
  space_id: string
  /** 第一次写上锁定的版本。之后只对这个版本重复套用 */
  version_id?: string
}

const memory = new Map<string, CreateSpaceLock>()

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

function readAll(): Record<string, CreateSpaceLock> {
  const fromMemory = Object.fromEntries(memory)
  const raw = storage()?.getItem(STORAGE_KEY)
  if (!raw) return fromMemory
  try {
    const parsed = JSON.parse(raw) as Record<
      string,
      { space_id?: unknown; version_id?: unknown }
    >
    const fromStorage: Record<string, CreateSpaceLock> = {}
    for (const [app_id, lock] of Object.entries(parsed)) {
      if (typeof lock?.space_id !== 'string' || !lock.space_id.trim()) continue
      const version_id =
        typeof lock.version_id === 'string' && lock.version_id.trim()
          ? lock.version_id.trim()
          : undefined
      fromStorage[app_id] = {
        space_id: lock.space_id.trim(),
        ...(version_id ? { version_id } : {}),
      }
    }
    return { ...fromStorage, ...fromMemory }
  } catch {
    return fromMemory
  }
}

function writeAll(all: Record<string, CreateSpaceLock>) {
  memory.clear()
  for (const [app_id, lock] of Object.entries(all)) memory.set(app_id, lock)
  const box = storage()
  if (!box) return
  if (Object.keys(all).length === 0) box.removeItem(STORAGE_KEY)
  else box.setItem(STORAGE_KEY, JSON.stringify(all))
}

/** 空间后台创建成功后记住：这个应用的首版要锁定来源空间 */
export function rememberCreateSpaceLock(app_id: string, space_id: string) {
  const id = space_id.trim()
  if (!app_id || !id) return
  writeAll({ ...readAll(), [app_id]: { space_id: id } })
}

export function peekCreateSpaceLock(app_id: string): CreateSpaceLock | undefined {
  return readAll()[app_id]
}

/** 测试隔离 */
export function clearCreateSpaceLocks() {
  writeAll({})
}

/**
 * 首版写入空间自建并锁定所属空间。
 * 锁定留在本次会话里，并记下版本 id：刷新后仍只锁这一版，后续版本不动。
 */
export function applyPendingCreateSpaceLock(
  app_id: string,
  version: AppVersion,
): AppVersion {
  const lock = peekCreateSpaceLock(app_id)
  if (!lock) return version
  if (lock.version_id && lock.version_id !== version.id) return version
  if (!lock.version_id && version.is_first_version === false) return version
  const app_fixed_info = applyCreateSpaceContext(version.app_fixed_info, {
    space_id: lock.space_id,
    lockSpaceContext: true,
  })
  if (!app_fixed_info.distribution_locked) return version
  if (lock.version_id !== version.id) {
    writeAll({
      ...readAll(),
      [app_id]: { space_id: lock.space_id, version_id: version.id },
    })
  }
  return { ...version, app_fixed_info }
}
